/**
 * Покупки за Telegram Stars (MADR 0003): `POST /api/payments/invoice` → `Telegram.WebApp.openInvoice`. Начисляет только
 * сервер по вебхуку, поэтому после «paid» клиент несколько раз спрашивает состояние, пока покупка не появится. На сайте
 * Stars нет: кнопка открывает игру в Telegram.
 */
import type { OfferPlace, PlayerState, Product, ReviveBoostRequest, ReviveOffer } from "@newfungame/contracts";
import { analytics } from "../analytics";
import type { ApiTransport } from "../api";
import { setWindowActions } from "../ui/windows/actions";
import { fetchLoginBot } from "./login";
import { appStore, runRevived, setPlayer } from "./store";
import type { InvoiceStatus, TelegramWebApp } from "./telegram";

/** Метка `?startapp=` кнопки «Открыть в Telegram»: источник игрока. */
export const VIP_START_PARAM = "vip";
export const PAID_POLL_MS = 1000;
export const PAID_POLL_TRIES = 15;

export interface PurchaseDeps {
  api: ApiTransport;
  telegram: () => Promise<TelegramWebApp | null>;
  getPlayer(): PlayerState | null;
  setPlayer(p: PlayerState): void;
  /** Реванш применён или выбрано подкрепление: бой начинается заново с `p.run` (пока есть выбор — стоит под окном). */
  revived(p: PlayerState): void;
  botName(): Promise<string | null>;
  openUrl(url: string): void;
  wait(ms: number): Promise<void>;
}

/** Покупка видна в состоянии. Реванш — новая забежка: таймера могло и не быть (VIP). */
function delivered(product: Product, p: PlayerState, runId: string | undefined): boolean {
  if (product === "vip" || product === "starter") return p.vip.active;
  if (product === "season") return p.season?.pass ?? false;
  return product === "revive" ? p.run.runId !== runId : p.retryLock === null;
}

export function createPurchases(deps: PurchaseDeps) {
  const refresh = async (): Promise<PlayerState | null> => {
    try {
      const p = await deps.api.call("playerState", {});
      deps.setPlayer(p);
      return p;
    } catch {
      return null;
    }
  };

  /** Вебхук мог ещё не дойти: ждём покупку в состоянии игрока. */
  const awaitDelivery = async (product: Product, offer: ReviveOffer | null): Promise<void> => {
    const runId = deps.getPlayer()?.run.runId;
    for (let i = 0; i < PAID_POLL_TRIES; i++) {
      const p = await refresh();
      if (p && delivered(product, p, runId)) {
        if (offer) applied(p, offer);
        return;
      }
      await deps.wait(PAID_POLL_MS);
    }
  };

  const applied = (p: PlayerState, offer: ReviveOffer): void => {
    analytics().reviveStarted(offer);
    deps.revived(p);
  };

  /** Бесплатный реванш VIP: без счёта. */
  const reviveFree = async (offer: ReviveOffer, place: OfferPlace): Promise<void> => {
    analytics().purchaseClick("revive", place, true);
    try {
      const p = await deps.api.call("revive", {});
      analytics().purchaseResult("revive", place, "free");
      deps.setPlayer(p);
      applied(p, offer);
    } catch {
      analytics().purchaseResult("revive", place, "error");
      await refresh(); // окно закрылось: кнопка уйдёт по ответу сервера
    }
  };

  return {
    async buy(product: Product, place: OfferPlace): Promise<void> {
      const offer = product === "revive" ? (deps.getPlayer()?.revive ?? null) : null;
      if (offer?.stars === 0) return reviveFree(offer, place);
      analytics().purchaseClick(product, place, true);
      const tg = await deps.telegram();
      if (!tg?.openInvoice) {
        analytics().purchaseResult(product, place, "error");
        return;
      }
      let url: string;
      try {
        url = (await deps.api.call("createInvoice", { product })).url;
      } catch {
        analytics().purchaseResult(product, place, "error");
        await refresh(); // таймер кончился или VIP уже есть: окно выровняется по серверу
        return;
      }
      const status = await new Promise<InvoiceStatus>((resolve) => {
        try {
          tg.openInvoice!(url, resolve);
        } catch {
          resolve("failed");
        }
      });
      analytics().purchaseResult(product, place, status);
      if (status === "paid" || status === "pending") await awaitDelivery(product, offer);
    },

    /** Подкрепление после реванша (MADR 0019): выбор уходит на сервер, бой перезапускается с усилением. */
    async reviveBoost(pick: ReviveBoostRequest): Promise<void> {
      try {
        deps.revived(await deps.api.call("reviveBoost", pick));
      } catch {
        await refresh(); // вариант устарел (двойной тап, другая вкладка): окно выровняется по серверу
      }
    },

    /** Пакет показан: сервер больше его не предложит, открытое окно держит своё предложение. */
    starterShown(place: OfferPlace): void {
      analytics().starterOffer(place);
      const p = deps.getPlayer();
      if (p) deps.setPlayer({ ...p, starter: null });
      void deps.api.call("starterSeen", {}).catch(() => {}); // не дошло — предложим ещё раз
    },

    async openTelegram(place: OfferPlace): Promise<void> {
      analytics().purchaseClick("vip", place, false);
      const bot = await deps.botName();
      if (bot) deps.openUrl(`https://t.me/${bot}?startapp=${VIP_START_PARAM}`);
    },
  };
}

/** Подключает кнопки покупок окон. */
export function setupPayments(api: ApiTransport, telegram: Promise<TelegramWebApp | null>): void {
  // отладка окон (`?mock=1`) пишет вызовы в журнал сама — не перекрываем её колбэки
  if (window.__nfgWindows) return;
  let bot: Promise<string | null> | null = null;
  const purchases = createPurchases({
    api,
    telegram: () => telegram,
    getPlayer: () => appStore.get().player,
    setPlayer: (p) => setPlayer(p),
    revived: (p) => runRevived(p),
    botName: () => (bot ??= fetchLoginBot()),
    openUrl: (url) => window.open(url, "_blank", "noopener"),
    wait: (ms) => new Promise((r) => setTimeout(r, ms)),
  });
  setWindowActions({
    buy: purchases.buy,
    openTelegram: (place) => void purchases.openTelegram(place),
    offerShown: (place) => analytics().offerView(place),
    reviveShown: (offer) => analytics().reviveOffer(offer),
    reviveBoost: purchases.reviveBoost,
    starterShown: purchases.starterShown,
  });
}
